import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { ScoreDial } from "../components/ScoreDial";
import { WhatIfSimulator } from "../components/WhatIfSimulator";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { bandColor } from "../lib/bands";
import { CheckCircle2, AlertTriangle, Eye, Target, ArrowUpRight, TrendingUp, Download, Share2, Check, GitCompareArrows, Lock } from "lucide-react";

const SUB = [
  { key: "personal", label: "Personal Stability", color: "#DDA15E" },
  { key: "professional", label: "Professional Momentum", color: "#FF8A00" },
  { key: "financial", label: "Financial Health", color: "#5C6B4E" },
];

function SubBar({ label, value, color }) {
  return (
    <div data-testid={`subscore-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-ink">{label}</span>
        <span className="font-mono text-sm" style={{ color }}>{value}</span>
      </div>
      <div className="h-2.5 bg-clay rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: color }} initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} />
      </div>
    </div>
  );
}

function InsightList({ icon: Icon, title, items, color }) {
  return (
    <div className="card-soft p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} style={{ color }} />
        <h3 className="font-display font-bold text-ink">{title}</h3>
      </div>
      <ul className="space-y-2.5">
        {(items || []).map((it, i) => (
          <li key={i} className="text-sm text-stone leading-relaxed flex gap-2">
            <span style={{ color }}>—</span>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Results() {
  const { id } = useParams();
  const { subscription } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [completed, setCompleted] = useState([]);
  const [projected, setProjected] = useState(null);

  const toggleAction = async (index) => {
    const isDone = completed.includes(index);
    try {
      const { data } = await api.patch(`/assessments/${id}/actions`, { index, done: !isDone });
      setCompleted(data.completed_actions);
      setProjected({ core: data.projected_core, band: data.projected_band, gained: data.gained_points });
    } catch (e) {
      toast.error("Could not update action");
    }
  };

  const sharePage = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/assessments/${id}/share`);
      const link = `${window.location.origin}/s/${data.share_id}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Share link copied to clipboard");
      } catch {
        toast.success(link);
      }
    } catch (e) {
      toast.error("Could not create share link");
    } finally {
      setSharing(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/assessments/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `scorecaster-report-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      /* no-op */
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    api
      .get(`/assessments/${id}`)
      .then(({ data }) => {
        setData(data);
        setCompleted(data.completed_actions || []);
        setProjected({ core: data.projected_core, band: data.projected_band, gained: data.gained_points });
      })
      .catch(() => setError("Could not load this result."));
  }, [id]);

  if (error)
    return (
      <div className="min-h-screen grain">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-20 text-center text-stone">{error}</div>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen grain">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-20 text-center text-stone font-mono text-sm">Loading your score…</div>
      </div>
    );

  const { scores, fit, insight } = data;
  const fitColor = bandColor(fit.fit_band);

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 relative z-10">
        <span className="label-tiny">Your Scorecaster result</span>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2 mb-10 text-ink">Here's where you stand.</h1>

        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <div className="card-soft p-8 flex flex-col items-center justify-center">
            <ScoreDial score={scores.core} band={scores.band} />
            <p className="text-sm text-stone text-center mt-6 max-w-xs">{scores.band_meaning}</p>
          </div>

          <div className="card-soft p-8 flex flex-col justify-center gap-6">
            <span className="label-tiny">Pillar breakdown</span>
            {SUB.map((s) => (
              <SubBar key={s.key} label={s.label} value={scores[s.key]} color={s.color} />
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-3xl border border-clay bg-gradient-to-br from-[#FFF3E4] to-[#FDFBF7] p-8 shadow-[0_2px_12px_rgba(140,130,120,0.04)]" data-testid="decision-fit">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <span className="label-tiny">Decision fit</span>
              <h2 className="font-display font-bold text-xl md:text-2xl mt-2 text-ink">{fit.decision_label}</h2>
              <p className="text-sm text-stone mt-2">
                Estimated probability of success:{" "}
                <span className="font-mono" style={{ color: fitColor }}>{fit.probability}</span>
              </p>
            </div>
            <div className="text-right">
              <div className="font-mono text-5xl font-extrabold" style={{ color: fitColor }}>{fit.fit}</div>
              <span className="label-tiny" style={{ color: fitColor }}>{fit.fit_band}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 card-soft p-8">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={18} className="text-brand-orange" />
            <span className="label-tiny">AI read</span>
          </div>
          <p className="text-base md:text-lg text-ink leading-relaxed">{insight.summary}</p>
          {insight.ai_unavailable && (
            <p className="text-xs text-stone/70 mt-3 font-mono">AI narrative temporarily unavailable — showing baseline guidance.</p>
          )}
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <InsightList icon={CheckCircle2} title="Strengths" items={insight.strengths} color="#5C6B4E" />
          <InsightList icon={AlertTriangle} title="Vulnerabilities" items={insight.vulnerabilities} color="#DDA15E" />
          <InsightList icon={Eye} title="Blind spots" items={insight.blind_spots} color="#FF4D4D" />
        </div>

        {/* action plan */}
        <div className="mt-6 card-soft p-8" data-testid="action-plan">
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div className="flex items-center gap-2">
              <Target size={18} className="text-brand-orange" />
              <h2 className="font-display font-bold text-xl text-ink">Your action plan</h2>
            </div>
            {projected && (
              <div className="text-right" data-testid="projected-score">
                <span className="label-tiny">Projected score</span>
                <div className="flex items-baseline gap-2 justify-end">
                  <span className="font-mono text-2xl font-bold" style={{ color: bandColor(projected.band) }}>{projected.core}</span>
                  {projected.gained > 0 && <span className="font-mono text-xs text-moss">▲ {projected.gained}</span>}
                </div>
              </div>
            )}
          </div>

          {projected && (
            <div className="mb-6">
              <div className="relative h-2.5 bg-clay rounded-full overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-[#D8C6AE] rounded-full" style={{ width: `${scores.core}%` }} />
                <motion.div className="absolute inset-y-0 left-0 rounded-full" style={{ background: "linear-gradient(90deg, #FF4D4D, #FF8A00)" }} animate={{ width: `${projected.core}%` }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.7 }} />
              </div>
              <div className="flex justify-between mt-2 text-[0.7rem] text-stone/70">
                <span>Now {scores.core}</span>
                <span>Complete every step to reach {Math.min(100, Math.round(scores.core + (insight.action_plan || []).reduce((s, a) => s + (parseInt(String(a.impact).match(/\d+/)?.[0] || 0)), 0)))}</span>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {(insight.action_plan || []).map((a, i) => {
              const done = completed.includes(i);
              return (
                <button key={i} onClick={() => toggleAction(i)} data-testid={`action-item-${i}`}
                  className={`w-full text-left flex items-center justify-between gap-4 border rounded-2xl px-5 py-4 transition-all duration-300 ${done ? "border-moss/40 bg-moss/5" : "border-clay hover:border-[#DDA15E]"}`}>
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-lg border transition-all duration-300 ${done ? "bg-moss border-moss scale-110" : "border-[#C9BCA8]"}`}>
                      {done && <Check size={13} className="text-white" />}
                    </span>
                    <div>
                      <p className={`text-sm ${done ? "text-stone line-through" : "text-ink"}`}>{a.action}</p>
                      <span className="text-xs text-stone/70">{a.timeframe}</span>
                    </div>
                  </div>
                  <span className="font-mono text-sm text-moss whitespace-nowrap">{a.impact}</span>
                </button>
              );
            })}
          </div>
        </div>

        <WhatIfSimulator baseScores={scores} baseFit={fit} decisionContext={data.decision_context} />

        <Link to={subscription?.active ? "/compare" : "/premium"} data-testid="results-compare-hook"
          className="mt-6 rounded-3xl border border-brand-orange/30 bg-gradient-to-br from-[#FFF3E4] to-[#FDFBF7] p-6 flex items-center justify-between gap-4 hover:-translate-y-0.5 transition-transform duration-500 group">
          <div className="flex items-start gap-3">
            {subscription?.active ? <GitCompareArrows size={20} className="text-brand-orange mt-0.5 shrink-0" /> : <Lock size={18} className="text-brand-orange mt-0.5 shrink-0" />}
            <div>
              <p className="font-display font-bold text-ink">
                {subscription?.active ? "Compare this profile against other decisions" : "See how this profile fits other decisions"}
              </p>
              <p className="text-sm text-stone mt-1">
                {subscription?.active ? "Rank career change, business, home, and more — side by side." : "Premium: rank multiple decisions side-by-side and find your best fit."}
              </p>
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-orange text-white font-semibold text-sm group-hover:bg-[#e67e00] transition-colors duration-300">
            {subscription?.active ? "Compare" : "Unlock"} <ArrowUpRight size={15} />
          </span>
        </Link>

        <div className="mt-10 flex flex-wrap gap-4">
          <button onClick={downloadPdf} disabled={downloading} data-testid="results-download-pdf"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300 disabled:opacity-60">
            <Download size={16} /> {downloading ? "Preparing…" : "Download PDF"}
          </button>
          <button onClick={sharePage} disabled={sharing} data-testid="results-share"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-clay text-ink hover:border-[#DDA15E] transition-colors duration-300 disabled:opacity-60">
            <Share2 size={16} /> {sharing ? "Creating…" : "Share link"}
          </button>
          <Link to="/dashboard" data-testid="results-dashboard" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-white font-semibold hover:bg-[#413b36] transition-colors duration-300">
            View dashboard <ArrowUpRight size={16} />
          </Link>
          <Link to="/survey" data-testid="results-retake" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-clay text-ink hover:border-[#DDA15E] transition-colors duration-300">
            Take again
          </Link>
        </div>
      </div>
    </div>
  );
}
